import React from 'react';
import { gridSize } from '@atlaskit/theme';
import styled from '@emotion/styled';
import { FormattedMessage } from 'react-intl';
import Heading from '@atlaskit/heading';
import { Box, Stack } from '@atlaskit/primitives';
import WhiteboxPage from '../../../components/WhiteboxPage';
import TallButton from '../../../components/TallButton';
import ButtonContainer from '../../../components/ButtonContainer';
import { AidFrontendFeatureGate } from '../../../providers/FeatureGates';
import { checkFeatureGateOnceInitialized } from '../../../utilities/feature-flags/feature-gate';
import { fetchPageState } from '../../../utilities/hooks/useGlobalPageState';
import mfaEnableImgSrc from '../../../images/two-step-verification/mfa-enable.png';
import messages from './messages/content';
import { pageId } from './constants';
import {
  useMfaPromotePageSetup,
  useEnableTwoStepVerification,
  useDismissTwoStepVerification,
  useCreatePasskey,
  useHasPasskeys,
} from './hooks';
import { TwoStepVerificationMoreInfoLink, PasskeysMoreInfoLink } from './components';

const MfaPromoteContainer = styled.div`
  margin-top: ${gridSize()}px;
`;

const MfaImg = styled.img`
  height: ${gridSize() * 14}px;
  display: block;
  margin: 0 auto;
  padding: ${gridSize()}px 0;
`;

const ContentBody = styled.p`
  margin: ${gridSize()}px 0;
`;

export async function securityScreenLoader({ request }: { request: Request }) {
  if (!(await checkFeatureGateOnceInitialized(AidFrontendFeatureGate.USE_STATE_DATA_LOADERS))) {
    return null;
  }

  return await fetchPageState('/rest/login/security-screen/state', request, {
    capabilities: ['capability-group--login', 'capability-name--mfa-promote'],
  });
}

export const MfaPromotePage = () => {
  useMfaPromotePageSetup();

  const { onEnableTwoStepVerificationClicked: onEnableTwoStepVerification } =
    useEnableTwoStepVerification();
  const { onDismissTwoStepVerificationClicked: onDismissTwoStepVerification } =
    useDismissTwoStepVerification();
  const { onCreatePasskeyClicked: onCreatePasskey } = useCreatePasskey();
  const { hasPasskeys } = useHasPasskeys();

  return (
    <WhiteboxPage
      pageId={pageId}
      header={
        <Heading as="h1" size="small">
          <FormattedMessage {...messages.promoteMfaPasskeysHeader} />
        </Heading>
      }
      whiteboxFooter={
        <Stack space="space.100">
          <TwoStepVerificationMoreInfoLink />
          <PasskeysMoreInfoLink isPasskeyShown={!hasPasskeys} />
        </Stack>
      }>
      <MfaPromoteContainer>
        <MfaImg src={mfaEnableImgSrc} alt="" />
        <ContentBody>
          <FormattedMessage {...messages.promoteMfaMessageBody} />
        </ContentBody>
        <ButtonContainer key="mfa.promote.continue.container">
          <TallButton
            appearance="primary"
            id="mfa-promote-continue"
            type="submit"
            isFullWidth
            onClick={onEnableTwoStepVerification}>
            <FormattedMessage {...messages.promoteMfaContinue} />
          </TallButton>
        </ButtonContainer>
        <ButtonContainer key="mfa.promote.dismiss.container">
          <TallButton
            appearance="default"
            id="mfa-promote-dismiss"
            testId="mfa-promote-dismiss-idf-testid"
            type="submit"
            isFullWidth
            onClick={onDismissTwoStepVerification}>
            <FormattedMessage {...messages.promoteMfaDismiss} />
          </TallButton>
        </ButtonContainer>

        {!hasPasskeys && (
          <Box paddingBlockStart="space.300">
            <ContentBody>
              <FormattedMessage {...messages.promotePasskeysMessageBody} />
            </ContentBody>
            <ButtonContainer key="passkeys.promote.continue.container">
              <TallButton
                appearance="default"
                id="passkeys-promote-continue"
                testId="mfa-promote-passkey-idf-testid"
                type="submit"
                isFullWidth
                onClick={onCreatePasskey}>
                <FormattedMessage {...messages.promotePasskeysContinue} />
              </TallButton>
            </ButtonContainer>
          </Box>
        )}
      </MfaPromoteContainer>
    </WhiteboxPage>
  );
};

export default MfaPromotePage;
